import * as XLSX from "xlsx";

/**
 * Columna de exportación · `header` es el encabezado visible en Excel y
 * `value` extrae el valor de la fila (string/number/boolean/Date/null).
 */
export interface ColumnaExcel<T> {
  header: string;
  value: (fila: T) => string | number | boolean | Date | null | undefined;
}

/**
 * Genera y descarga un .xlsx real (no CSV renombrado) con una hoja única.
 * Anchos de columna auto-calculados sobre el contenido (cap a 60 chars).
 * El nombre del archivo lleva la fecha para no pisar descargas previas.
 */
export function exportarExcel<T>({
  archivo,
  hoja = "Datos",
  columnas,
  filas,
}: {
  /** Nombre base sin extensión · ej. "mis-solicitudes". */
  archivo: string;
  hoja?: string;
  columnas: ColumnaExcel<T>[];
  filas: T[];
}): void {
  const encabezados = columnas.map((c) => c.header);
  const datos = filas.map((f) =>
    columnas.map((c) => {
      const v = c.value(f);
      return v === undefined ? null : v;
    })
  );

  const sheet = XLSX.utils.aoa_to_sheet([encabezados, ...datos]);

  // Ancho por columna = max(header, contenido) acotado a [10, 60].
  sheet["!cols"] = columnas.map((c, i) => {
    let max = c.header.length;
    for (const fila of datos) {
      const len = String(fila[i] ?? "").length;
      if (len > max) max = len;
    }
    return { wch: Math.min(Math.max(max + 2, 10), 60) };
  });

  const libro = XLSX.utils.book_new();
  // Nombre de hoja: Excel limita a 31 chars y prohíbe : \ / ? * [ ]
  XLSX.utils.book_append_sheet(libro, sheet, hoja.replace(/[:\\/?*[\]]/g, " ").slice(0, 31));

  const fecha = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(libro, `${archivo}_${fecha}.xlsx`);
}
